import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Input, Modal, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { m503Api, PendingShipment } from '../../services/m503Api';
import { BatchAcceptanceBridge, pwm503f1AcceptanceManifest } from '../../features/acceptance';
import './M503Form.css';

const M503Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<PendingShipment[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const allSelected = items.length > 0 && selected.length === items.length;

  const query = async () => {
    if (!startNo.trim() && !endNo.trim()) {
      message.warning('請至少輸入一個交運單號範圍');
      return;
    }
    setLoading(true);
    try {
      const result = await m503Api.query(startNo.trim(), endNo.trim());
      setLastApiResponse(result);
      setItems(result.items || []);
      setSelected([]);
      if (!result.total) message.info('查無可核准的交運單');
    } catch (error: any) {
      message.error(error?.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  };

  const approve = () => {
    if (!selected.length) {
      message.warning('請選擇要核准的交運單');
      return;
    }
    Modal.confirm({
      title: '確認核准',
      content: `即將以原子交易核准 ${selected.length} 張交運單；任一張失敗時全部不寫入。`,
      okText: '確認核准',
      cancelText: '取消',
      onOk: async () => {
        setLoading(true);
        try {
          const result = await m503Api.execute(selected);
          setLastApiResponse(result);
          message.success(result.message);
          await query();
        } catch (error: any) {
          message.error(error?.response?.data?.error || '核准失敗，資料未寫入');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns: ColumnsType<PendingShipment> = [
    {
      title: <Checkbox checked={allSelected} onChange={(e) => setSelected(e.target.checked ? items.map((x) => x.fm130_01) : [])} />,
      width: 48,
      render: (_, item) => (
        <Checkbox
          checked={selectedSet.has(item.fm130_01)}
          onChange={(e) => setSelected((old) => e.target.checked ? [...old, item.fm130_01] : old.filter((x) => x !== item.fm130_01))}
        />
      ),
    },
    { title: <span data-contract-field="fm130_01">交運單號</span>, dataIndex: 'fm130_01', width: 120 },
    { title: <span data-contract-field="fm130_11">交運日期</span>, dataIndex: 'fm130_11', width: 110 },
    { title: <span data-contract-field="fm130_16">客戶代號</span>, dataIndex: 'fm130_16', width: 130 },
    { title: <span data-contract-field="fm130_12">交運數量</span>, dataIndex: 'fm130_12', width: 110 },
    { title: <span data-contract-field="fm130_20">部門</span>, key: 'department', render: (_, x) => x.dept_name ? `${x.fm130_20} - ${x.dept_name}` : x.fm130_20 },
    { title: <span data-contract-field="fm130_14">交運總額</span>, dataIndex: 'fm130_14', width: 120 },
  ];
  const queryAcceptanceExact = (id: string) => m503Api.query(id, id);

  return (
    <div className="m503-form">
      <header><h1>庫存交運清單核准 <span>M503</span></h1></header>
      <section className="m503-query">
        <label data-contract-field="query_shipment_from">交運單號</label>
        <Input data-contract-field="query_shipment_from" value={startNo} onChange={(e) => setStartNo(e.target.value)} placeholder="起始單號" />
        <label data-contract-field="query_shipment_to">至</label>
        <Input data-contract-field="query_shipment_to" value={endNo} onChange={(e) => setEndNo(e.target.value)} placeholder="結束單號" />
        <Button data-acceptance-action="PWM503-ACT-QUERY" type="primary" onClick={query} loading={loading}>查詢</Button>
      </section>
      <Table rowKey="fm130_01" columns={columns} dataSource={items} pagination={false} loading={loading} scroll={{ y: 440 }} />
      <footer>
        <Button data-acceptance-action="PWM503-ACT-APPROVE-BATCH" type="primary" size="large" onClick={approve} disabled={!selected.length} loading={loading}>核准（{selected.length}）</Button>
        <Button size="large" onClick={() => navigate('/erp')}>離開</Button>
      </footer>
      <BatchAcceptanceBridge
        manifest={pwm503f1AcceptanceManifest}
        records={items}
        selectedKeys={selected}
        keyOf={(item) => item.fm130_01}
        queryExact={queryAcceptanceExact}
        lastApiResponse={lastApiResponse}
        loading={loading}
        onAutoFill={() => { if (items[0]) setSelected([items[0].fm130_01]); }}
        autoFillDisabled={items.length === 0}
        autoFillReason="先查詢候選交運單，再安全勾選第一筆；不會自動核准。"
        screenDraft={{ startNo, endNo, selected_items: selected, candidates: items }}
      />
    </div>
  );
};

export default M503Form;

